import { useId, useState } from "react";
import { Icon, type IconName } from "../icons/Icon";
import { Avatar } from "../primitives/Avatar";
import { cx } from "../utils/format";

export interface LookupOption { id: string; title: string; sub?: string; initials?: string; icon?: IconName; }

/** A field that finds one thing while you type (a customer by name or address) and then holds it as a chip.
 *  The app filters: it hands in the options for the current query. Arrows move, Enter takes, Escape closes the list. */
export function Lookup({ label, placeholder, query, onQuery, options, onPick, picked, onClear, empty, autoFocus }: {
  label: string; placeholder?: string; query: string; onQuery: (q: string) => void; options: LookupOption[]; onPick: (id: string) => void;
  /** what was chosen; shown in place of the input until cleared */ picked?: LookupOption | null; onClear: () => void;
  /** said under the input when something is typed and nothing matches */ empty?: string; autoFocus?: boolean;
}) {
  const listId = useId();
  const [sel, setSel] = useState(0);
  const [open, setOpen] = useState(false);
  if (picked) {
    return (
      <span className="ob-lookup ob-lookup--picked">
        <span className="ob-lookup__chip">
          {picked.initials && <Avatar size="sm" initials={picked.initials} />}
          <span className="ob-lookup__text"><span className="ob-lookup__title">{picked.title}</span>{picked.sub && <span className="ob-lookup__sub">{picked.sub}</span>}</span>
          <button type="button" className="ob-lookup__clear" aria-label={`Remove ${picked.title}`} onClick={onClear}><Icon name="x" size="xs" /></button>
        </span>
      </span>
    );
  }
  const showList = open && query.trim().length > 0;
  const active = Math.min(sel, Math.max(0, options.length - 1));
  return (
    <span className="ob-lookup">
      <input className="ob-lookup__input" role="combobox" aria-label={label} aria-expanded={showList} aria-controls={listId} aria-autocomplete="list" placeholder={placeholder} value={query} autoFocus={autoFocus}
        onChange={(e) => { onQuery(e.target.value); setSel(0); setOpen(true); }} onFocus={() => setOpen(true)} onBlur={() => setOpen(false)}
        onKeyDown={(e) => {
          if (!showList) return;
          if (e.key === "ArrowDown") { e.preventDefault(); setSel((active + 1) % Math.max(1, options.length)); }
          else if (e.key === "ArrowUp") { e.preventDefault(); setSel((active - 1 + options.length) % Math.max(1, options.length)); }
          else if (e.key === "Enter" && options[active]) { e.preventDefault(); onPick(options[active]!.id); }
          else if (e.key === "Escape") { e.stopPropagation(); setOpen(false); }
        }} />
      {showList && (
        <span className="ob-lookup__list" role="listbox" id={listId} aria-label={label}>
          {options.map((o, i) => (
            <button type="button" key={o.id} role="option" aria-selected={i === active} className={cx("ob-lookup__option", i === active && "ob-lookup__option--active")} onMouseEnter={() => setSel(i)} onMouseDown={(e) => { e.preventDefault(); onPick(o.id); }}>
              {o.initials ? <Avatar size="sm" initials={o.initials} /> : <span className="ob-lookup__icon"><Icon name={o.icon ?? "user"} size="sm" /></span>}
              <span className="ob-lookup__text"><span className="ob-lookup__title">{o.title}</span>{o.sub && <span className="ob-lookup__sub">{o.sub}</span>}</span>
            </button>
          ))}
          {options.length === 0 && empty && <span className="ob-lookup__empty">{empty}</span>}
        </span>
      )}
    </span>
  );
}
